import { useEffect, useState } from 'react';

import { Flex } from '@revenuemarket/cds';
import { Modal, ModalProps, Typography } from 'antd';
import dayjs from 'dayjs';

import { useGetTransactionLabelMappings } from '~hooks/queries/mapping/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { CashFlowType } from '~models/cashFlow';
import { mixpanel } from '~pages/_app';

import MappingTaxInvoiceList from './MappingTaxInvoiceList';
import TransactionMappingTargetList from './TransactionMappingTargetList';

interface Props extends ModalProps {
  transactionId: number;
  showTaxInvoice?: boolean;
  onMappingComplete?: () => void;
  onCancel?: () => void;
}

const TransactionMappingModal: React.FC<Props> = ({
  showTaxInvoice,
  onMappingComplete,
  onCancel,
  ...props
}) => {
  const [toggleShowTaxInvoice, setToggleShowTaxInvoice] = useState<boolean>(
    showTaxInvoice ?? false,
  );
  const { companyId } = useDemoAwareCompanyId();
  const { data } = useGetTransactionLabelMappings({
    companyId,
    transactionId: props.transactionId,
  });

  const cashFlowType =
    (data?.Data?.data?.transactionAmount ?? 0) > 0 ? CashFlowType.IN : CashFlowType.OUT;

  useEffect(() => {
    mixpanel.track(MIXPANEL_EVENT.VIEW.TAXINVOICE_MAPPING_AND_SPLIT_MODAL);
  }, []);

  return (
    <Modal
      width={toggleShowTaxInvoice ? '90vw' : '540px'}
      open={props.open}
      onCancel={() => {
        onCancel?.();
      }}
      footer={null}
      title={
        <Flex direction="column" gap={4}>
          <Typography.Title level={5}>거래내역 증빙/분할</Typography.Title>
          <Typography.Text
            style={{
              fontWeight: 400,
              fontSize: 14,
            }}
          >
            세금계산서 증빙을 추가하면 거래내역이 자동으로 분할됩니다. 만약 세금계산서가 발행되지
            않는 거래내역이라면 직접 금액을 입력하여 거래내역을 분할할 수 있어요.
          </Typography.Text>
        </Flex>
      }
    >
      <Flex
        gap={24}
        style={{
          paddingTop: 16,
        }}
      >
        {toggleShowTaxInvoice && (
          <Flex direction="column" gap={16} style={{ width: '60%' }}>
            <MappingTaxInvoiceList
              cashFlowType={cashFlowType}
              transactionAt={dayjs(data?.Data?.data?.transactionAt).format('YYYY-MM-DD')}
            />
          </Flex>
        )}
        <Flex
          direction="column"
          style={{
            width: toggleShowTaxInvoice ? '40%' : '100%',
          }}
        >
          <TransactionMappingTargetList
            onClose={() => {
              if (toggleShowTaxInvoice) {
                setToggleShowTaxInvoice(false);
                return;
              }

              onCancel?.();
            }}
            transactionId={props.transactionId}
            mode={toggleShowTaxInvoice ? 'FULL' : 'FOLD'}
            onChangeMode={() => setToggleShowTaxInvoice(!toggleShowTaxInvoice)}
            onMappingComplete={() => {
              onCancel?.();
            }}
          />
        </Flex>
      </Flex>
    </Modal>
  );
};

export default TransactionMappingModal;
